import Notification from '@/orm/models/Notification/Notification';
import { ensureCsrfCookie } from '@/api';
import Company from '../Company/Company';
import BaseModel from '../BaseModel';
import Settings from './Settings';

export default class User extends BaseModel {
  static entity = 'users';

  static primaryKey = 'id';

  static fields() {
    return {
      id: this.attr(null),
      email: this.string(null).nullable(),
      firstName: this.string(null).nullable(),
      lastName: this.string(null).nullable(),
      phone: this.string(null).nullable(),
      emailVerified: this.boolean(null).nullable(),
      showDashboardTip: this.boolean(null).nullable(),
      settings: this.hasOne(Settings, 'userId'),
      company: this.belongsTo(Company, 'companyId'),
      companyId: this.attr(null),
      notification: this.morphMany(Notification, 'notifiableId', 'notifiableType'),
      position: this.attr(null),
      isSuperUser: this.attr(null),
      isCurrentUser: this.attr(null),
      hasActiveVacancies: this.attr(null),
      hasArchiveVacancies: this.attr(null),
      createdUid: this.string(null).nullable(),
    };
  }

  static getAuthUser() {
    return this.query().withAllRecursive().where('isCurrentUser', true).first();
  }

  static apiConfig = {
    actions: {
      async login(login, password, remember) {
        const result = await this.post('users/login', { login, password, remember }, { save: false });
        return result;
      },
      async logout() {
        await ensureCsrfCookie();
        await this.post('users/logout', null, { save: false });
      },
      async fetch() {
        try {
          return await this.get('/users');
        } catch (e) {
          if (e.name === 'ApiError' && e.is(401)) {
            return null;
          }
          throw e;
        }
      },
      setPassword(payload) {
        return this.put('users/activate', payload, { save: false });
      },
      update(payload) {
        return this.patch('users', payload);
      },
      forgotPassword(payload) {
        return this.post('users/forgot-password', payload, { save: false });
      },
      changePassword(payload) {
        return this.post('users/change-password', payload, { save: false });
      },
      resetPassword(payload) {
        return this.post('users/reset-password', payload, { save: false });
      },
      verifyEmail(payload) {
        return this.post('users/verify-email', null, { params: payload, save: false });
      },
      verifyEmailResend(payload) {
        return this.post('users/resend-verification-email', payload, { save: false });
      },
      getUsers() {
        return this.get('/users/index');
      },
      deleteUser(id, payload) {
        return this.post(`/users/delete/${id}`, payload);
      },
      createUser(payload) {
        return this.post('/users/create', payload, { save: false });
      },
      updateUser(id, payload) {
        return this.patch(`/users/${id}`, payload);
      },
      updateNewUser(uid, payload) {
        return this.post(`/users/update_new_user/${uid}`, payload);
      },
      updateAndChangePassword(payload) {
        return this.post('/users/update_with_password', payload);
      },
      fetchUserByCreatedUid(uid) {
        return this.get(`/users/by_created_uid/${uid}`);
      },
    },
  }
}
